import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import { Check, ArrowRight, Zap, Star, Crown, Rocket, HelpCircle } from 'lucide-react';

const packages = [
  {
    id: 'starter',
    name: 'Starter',
    price: 5,
    credits: 50,
    icon: Zap,
    color: 'from-sky-500/20 to-sky-500/5',
    borderColor: 'border-white/5',
    features: ['50 credits', '25 AI assessments', 'Basic analytics', 'Email support'],
    cta: 'Get Started',
  },
  {
    id: 'basic',
    name: 'Basic',
    price: 9,
    credits: 100,
    icon: Star,
    color: 'from-indigo-500/20 to-indigo-500/5',
    borderColor: 'border-white/5',
    features: ['100 credits', '50 AI assessments', 'Full analytics', 'Priority support', 'Custom SOPs'],
    cta: 'Get Started',
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 20,
    credits: 250,
    icon: Crown,
    popular: true,
    color: 'from-[#FF8C00]/20 to-[#FF3366]/10',
    borderColor: 'border-[#FF8C00]/40',
    features: ['250 credits', '125 AI assessments', 'Advanced reporting', 'Team management', 'Live monitoring', 'Audit sheets'],
    cta: 'Get Started',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 35,
    credits: 500,
    icon: Rocket,
    color: 'from-emerald-500/20 to-emerald-500/5',
    borderColor: 'border-white/5',
    features: ['500 credits', '250 AI assessments', 'Everything in Professional', 'Dedicated support', 'Custom integrations', 'SLA guarantee'],
    cta: 'Contact Sales',
  },
];

const faqs = [
  {
    q: 'What are credits?',
    a: 'Credits are the currency used to run assessments. AI-powered assessments use 2 credits each, while manual audits use 1 credit.',
  },
  {
    q: 'Can I buy more credits anytime?',
    a: 'Yes! You can purchase additional credit packages anytime from your dashboard. Credits never expire.',
  },
  {
    q: 'Is there a free trial?',
    a: 'New accounts start with 100 free credits. This gives you 50 AI assessments to evaluate the platform.',
  },
  {
    q: 'What payment methods do you accept?',
    a: 'We accept all major credit and debit cards through our secure Stripe payment integration.',
  },
];

const PricingPage = () => {
  return (
    <div className="bg-[#050505] min-h-screen text-white" data-testid="pricing-page">
      <PublicNav />

      {/* Hero */}
      <section className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-4">Pricing</p>
            <h1
              className="text-5xl md:text-6xl font-medium tracking-tighter leading-[1.1] mb-6"
              style={{ fontFamily: "'Clash Display', sans-serif" }}
            >
              Simple, credit-based{' '}
              <span className="bg-gradient-to-r from-[#FF3366] via-[#FF8C00] to-[#FFD700] bg-clip-text text-transparent">
                pricing
              </span>
            </h1>
            <p className="text-lg text-zinc-400 max-w-xl mx-auto" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Pay only for what you use. No monthly fees, no hidden charges. Buy credits and use them at your pace.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Grid */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {packages.map((pkg, i) => {
              const Icon = pkg.icon;
              return (
                <motion.div
                  key={pkg.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  className={`relative rounded-3xl bg-gradient-to-b ${pkg.color} border ${pkg.borderColor} p-8 flex flex-col ${
                    pkg.popular ? 'lg:scale-105 z-10 shadow-[0_0_40px_rgba(255,140,0,0.12)]' : ''
                  }`}
                  data-testid={`pricing-card-${pkg.id}`}
                >
                  {pkg.popular && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                      <span className="bg-gradient-to-r from-[#FF3366] to-[#FF8C00] text-white text-xs font-bold px-4 py-1.5 rounded-full">
                        Most Popular
                      </span>
                    </div>
                  )}

                  <div className="mb-6">
                    <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center mb-4">
                      <Icon size={24} className={pkg.popular ? 'text-[#FF8C00]' : 'text-zinc-400'} />
                    </div>
                    <h3 className="text-xl font-semibold text-white">{pkg.name}</h3>
                  </div>

                  <div className="mb-6">
                    <span className="text-4xl font-bold text-white">${pkg.price}</span>
                    <span className="text-zinc-500 ml-1">/ {pkg.credits} credits</span>
                  </div>

                  <ul className="space-y-3 mb-8 flex-1">
                    {pkg.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5">
                        <Check size={16} className={pkg.popular ? 'text-[#FF8C00] mt-0.5' : 'text-zinc-500 mt-0.5'} />
                        <span className="text-sm text-zinc-300">{f}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to={pkg.id === 'enterprise' ? '/contact' : '/login?tab=register'}
                    className={`block text-center rounded-full py-3 font-semibold text-sm transition-all ${
                      pkg.popular
                        ? 'bg-gradient-to-r from-[#FF3366] to-[#FF8C00] text-white hover:scale-105 shadow-[0_0_20px_rgba(255,51,102,0.3)]'
                        : 'bg-white/5 border border-white/10 text-white hover:bg-white/10'
                    }`}
                    data-testid={`pricing-cta-${pkg.id}`}
                  >
                    {pkg.cta}
                  </Link>
                </motion.div>
              );
            })}
          </div>

          {/* Credit Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 rounded-2xl bg-[#0F0F11] border border-white/5 p-8 flex flex-col md:flex-row items-center justify-center gap-8"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#FF3366]/10 flex items-center justify-center">
                <Zap size={20} className="text-[#FF3366]" />
              </div>
              <div>
                <p className="text-white font-semibold">2 credits</p>
                <p className="text-xs text-zinc-500">per AI assessment</p>
              </div>
            </div>
            <div className="w-px h-10 bg-white/10 hidden md:block" />
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#FF8C00]/10 flex items-center justify-center">
                <Star size={20} className="text-[#FF8C00]" />
              </div>
              <div>
                <p className="text-white font-semibold">1 credit</p>
                <p className="text-xs text-zinc-500">per manual audit</p>
              </div>
            </div>
            <div className="w-px h-10 bg-white/10 hidden md:block" />
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#FFD700]/10 flex items-center justify-center">
                <Crown size={20} className="text-[#FFD700]" />
              </div>
              <div>
                <p className="text-white font-semibold">100 free credits</p>
                <p className="text-xs text-zinc-500">on new account signup</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-[#0F0F11]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl md:text-4xl font-medium tracking-tight"
              style={{ fontFamily: "'Clash Display', sans-serif" }}
            >
              Frequently asked questions
            </h2>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="rounded-2xl bg-[#1A1A1D] border border-white/5 p-6"
                data-testid={`faq-item-${i}`}
              >
                <div className="flex items-start gap-3">
                  <HelpCircle size={18} className="text-[#FF8C00] mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-white font-semibold mb-2">{faq.q}</h4>
                    <p className="text-sm text-zinc-400 leading-relaxed">{faq.a}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default PricingPage;
